import { useLayoutEffect, useRef, useState, type JSX } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { R } from './detail/primitives.js'

/**
 * Step-through overlay that dims the screen and spotlights one element at a time.
 *
 * Extracted from the inbox's welcome tour so the filter drawer's "how do I set
 * filters?" guide is the same thing rather than a lookalike — one set of
 * positioning rules, one visual language.
 */
export interface SpotlightStep {
  /** `id` of the element to highlight. A step whose target is absent is skipped. */
  targetId: string
  title: string
  text: string
  placement?: 'top' | 'bottom'
}

export interface SpotlightGuideProps {
  steps: SpotlightStep[]
  stepIndex: number
  onNext: () => void
  onPrev: () => void
  onSkip: () => void
  /** Overridden by the filter guide, where "Skip tour" reads oddly. */
  skipLabel?: string
  /** Raised above the filter drawer, which already sits at z-40. */
  zIndexClass?: string
}

const CARD_WIDTH = 320
const CARD_HEIGHT = 168

export default function SpotlightGuide({
  steps,
  stepIndex,
  onNext,
  onPrev,
  onSkip,
  skipLabel = 'Skip tour',
  zIndexClass = 'z-[100]',
}: SpotlightGuideProps): JSX.Element | null {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const step = steps[stepIndex]
  // Which way the user was travelling when they arrived at this step, so a
  // skipped step is stepped *over* rather than always forward. Skipping forward
  // out of a Back press would bounce the user straight back to where they came
  // from, making Back look broken.
  const previousIndexRef = useRef(stepIndex)

  useLayoutEffect(() => {
    if (!step) return
    const goingBack = stepIndex < previousIndexRef.current
    previousIndexRef.current = stepIndex

    const el = document.getElementById(step.targetId)
    if (!el) {
      // Honour the documented contract and step over it. Rendering nothing
      // instead dead-ends the guide: the inbox tour opens on "New Conversation",
      // which is absent for anyone without `onNewConversation`, so pressing
      // Replay produced a blank screen with no Next to press.
      setRect(null)
      // Never call onPrev at index 0 — the caller clamps there, so the effect
      // would re-run on the same step forever.
      if (goingBack && stepIndex > 0) onPrev()
      else onNext()
      return
    }
    // The drawer scrolls, so bring the target into view before measuring —
    // otherwise the spotlight lands on an element the user cannot see.
    // Optional-called: not every environment implements it (jsdom does not),
    // and an exception here would abort the effect before `rect` is set,
    // silently rendering no guide at all.
    el.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    const update = (): void => { setRect(el.getBoundingClientRect()) }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [step, stepIndex])

  if (!step || !rect) return null

  const isFirst = stepIndex === 0
  const isLast = stepIndex === steps.length - 1
  const pad = 8
  const top = rect.top - pad
  const left = rect.left - pad
  const right = rect.right + pad
  const bottom = rect.bottom + pad
  const winW = window.innerWidth
  const winH = window.innerHeight

  let cardTop = step.placement === 'top' ? top - CARD_HEIGHT : bottom + 12
  let cardLeft = left
  if (cardLeft + CARD_WIDTH > winW - 16) cardLeft = winW - CARD_WIDTH - 16
  if (cardLeft < 16) cardLeft = 16
  if (cardTop < 16) cardTop = bottom + 12
  if (cardTop + CARD_HEIGHT > winH - 16) cardTop = top - CARD_HEIGHT

  return (
    <div className={`fixed inset-0 ${zIndexClass}`}>
      {/* Four panels around the target rather than one box with a hole — a
          box-shadow cut-out renders inconsistently across browsers. */}
      <div className="absolute inset-0" style={{ pointerEvents: 'none' }}>
        <div className="absolute left-0 right-0 bg-[#0f172a]/60" style={{ top: 0, height: `${top}px` }} />
        <div className="absolute left-0 right-0 bg-[#0f172a]/60" style={{ top: `${bottom}px`, bottom: 0 }} />
        <div className="absolute bg-[#0f172a]/60" style={{ top: `${top}px`, height: `${bottom - top}px`, left: 0, width: `${left}px` }} />
        <div className="absolute bg-[#0f172a]/60" style={{ top: `${top}px`, height: `${bottom - top}px`, left: `${right}px`, right: 0 }} />
        <div
          className={`absolute ${R} ring-2 ring-[#3366FF] ring-offset-2 ring-offset-transparent`}
          style={{ top: `${top}px`, left: `${left}px`, width: `${right - left}px`, height: `${bottom - top}px` }}
        />
      </div>

      <div
        className={`absolute w-80 ${R} bg-[#0f172a] p-5 text-white shadow-2xl`}
        style={{ top: `${cardTop}px`, left: `${cardLeft}px`, pointerEvents: 'auto' }}
      >
        <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-blue-300">
          Step {stepIndex + 1} of {steps.length}
        </div>
        <h4 className="mb-1.5 text-[15px] font-bold">{step.title}</h4>
        <p className="mb-5 text-[13px] leading-relaxed text-slate-300">{step.text}</p>
        <div className="flex items-center justify-between">
          <button onClick={onSkip} className="text-[12px] font-medium text-slate-400 transition-colors hover:text-white">
            {skipLabel}
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button onClick={onPrev} className={`flex items-center gap-1 ${R} border border-white/20 px-3 py-1.5 text-[12px] font-semibold transition-colors hover:bg-white/10`}>
                <ArrowLeft className="h-3.5 w-3.5" />Back
              </button>
            )}
            <button onClick={onNext} className={`flex items-center gap-1 ${R} bg-[#3366FF] px-4 py-1.5 text-[12px] font-bold transition-colors hover:bg-[#4477FF]`}>
              {isLast ? 'Finish' : 'Next'}<ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
